import { memo, useCallback } from 'react';

import { NotificationsItemLayout, notification } from '@air/component-notifications';
import { MegaphoneIcon } from '@air/design-icons';
import {
  type AnnouncementAction,
  AnnouncementBodyText,
  AnnouncementBottomButtons,
  type AnnouncementCommand,
  AnnouncementInlineLinks,
  AnnouncementMedia,
  filterAnnouncementActionsBySupportedCommands,
  narrowAnnouncementSettings,
  visibleAnnouncementActions,
} from '@air/feature-announcements';

import { NotificationIconBadge } from '~/components/AirNotifications/components/NotificationIconBadge';
import { formatCreatedAt } from '~/components/AirNotifications/utils/formatCreatedAt';
import { getAnnouncementMediaForApp } from '~/components/announcements/getAnnouncementMediaForApp';
import { useAnnouncementIcon } from '~/components/announcements/useAnnouncementIcon';
import { type PrivateAnnouncementListItem } from '~/components/v2/hooks/queries/usePrivateAnnouncementsList';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type AnnouncementNewsRowProps = {
  announcement: PrivateAnnouncementListItem;
  onAction: (announcement: PrivateAnnouncementListItem, action: AnnouncementAction) => void;
  supportedCommands: readonly AnnouncementCommand[] | undefined;
};

export const AnnouncementNewsRow = memo(({ announcement, onAction, supportedCommands }: AnnouncementNewsRowProps) => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const settings = narrowAnnouncementSettings(announcement);
  const hostIcon = useAnnouncementIcon(settings.style.icon);
  const Icon = hostIcon ?? MegaphoneIcon;

  // News can run workspace-level commands (open a blank canvas) but not canvas-page ones.
  const visible = visibleAnnouncementActions(
    filterAnnouncementActionsBySupportedCommands(settings.actions, supportedCommands),
  );
  const bodyText = announcement.body?.trim() ? announcement.body : null;
  const media = getAnnouncementMediaForApp(settings.media);
  const links = visible.filter((action) => action.type === 'link');
  const buttons = visible.filter((action) => action.type !== 'link');

  const handleAction = useCallback(
    (action: AnnouncementAction) => {
      onAction(announcement, action);
    },
    [announcement, onAction],
  );

  return (
    <NotificationsItemLayout
      className={notification({ className: 'ads:bg-base bg-gray-1' })}
      clampContent={false}
      content={
        <>
          {bodyText || links.length > 0 ? (
            // Block markdown is not valid inside a span, and the notification content slot is a div.
            <div>
              {bodyText ? <AnnouncementBodyText body={bodyText} /> : null}
              {links.length > 0 ? (
                <div className={bodyText ? 'mt-1' : undefined}>
                  <AnnouncementInlineLinks links={links} onAction={handleAction} />
                </div>
              ) : null}
            </div>
          ) : null}
          {media ? <AnnouncementMedia className="mt-2" media={media} /> : null}
          <AnnouncementBottomButtons buttons={buttons} onAction={handleAction} />
        </>
      }
      createdAt={formatCreatedAt(announcement.publishedAt)}
      isUI2={ui2026}
      prefix={<NotificationIconBadge icon={Icon} />}
      title={announcement.title}
    />
  );
});

AnnouncementNewsRow.displayName = 'AnnouncementNewsRow';
